import { DateTime } from "luxon";
import { useCallback, useMemo } from "react";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useRosterLookup } from "../../hooks/useRosterLookup";
import { useScheduleCacheEnabled } from "../../hooks/useScheduleWeekRules";
import { useScheduleWeeks } from "../../hooks/useScheduleWeeks";
import type { Nurse, ScheduleData } from "../../utils/models";
import { dayIndexOfDate, weekIndexOfDate } from "../../utils/panelWeeks";

const NO_NURSES: Nurse[] = [];

export type PatientsSchedule = {
	/** The weeks the census reads around the date, and the people the hospital demo names. */
	schedule: ScheduleData;
	weekIndexOf: (date: DateTime) => number;
	dayIndexOf: (date: DateTime) => number;
	/** The first and last day the date picker reaches. */
	firstDate: DateTime | undefined;
	lastDate: DateTime | undefined;
	isLoading: boolean;
	isError: boolean;
	refetch: () => void;
};

/**
 * What the Patients page reads around `date` (its census, and a chart from a week before to a week after). With
 * `staffing_schedule_cache` off, the old list and the provider's lookups, as before; on, the cached weeks around the
 * date, which hold every day the chart shows.
 */
export const usePatientsSchedule = (date: DateTime): PatientsSchedule => {
	const isCacheEnabled = useScheduleCacheEnabled();
	const { hospitalData, getWeekIndexByDate, getDayIndexByDate, latestDateInWeeksData, chosenDepartment } = useHospitalData();
	const panel = useScheduleWeeks(date);
	const { roster } = useRosterLookup(chosenDepartment.department_id);
	// DEFAULT: no one until the roster arrives; the weeks wait for it (`isLoading`).
	const cached = useMemo<ScheduleData>(() => ({ weeks: panel.weeks, nurses: roster ?? NO_NURSES }), [panel.weeks, roster]);
	const weekIndexOf = useCallback((day: DateTime) => weekIndexOfDate(panel.weeks, day), [panel.weeks]);
	const dayIndexOf = useCallback((day: DateTime) => dayIndexOfDate(panel.weeks, day), [panel.weeks]);

	if (!isCacheEnabled) {
		return {
			schedule: hospitalData,
			weekIndexOf: getWeekIndexByDate,
			dayIndexOf: getDayIndexByDate,
			firstDate: DateTime.min(...(hospitalData.weeks[0]?.weeklyShifts.flatMap((row) => row.days[0]?.date ?? []) ?? [])) ?? undefined,
			lastDate: latestDateInWeeksData,
			isLoading: false,
			isError: false,
			refetch: panel.refetch,
		};
	}
	return {
		schedule: cached,
		weekIndexOf,
		dayIndexOf,
		firstDate: panel.firstDate,
		lastDate: panel.lastDate,
		isLoading: panel.isLoading,
		isError: panel.isError,
		refetch: panel.refetch,
	};
};
